import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  Container,
  Divider,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconFilter,
  IconLockOpen,
  IconRefresh,
  IconSearch,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { useAllLocks, useForceReleaseLock } from "../../hooks/useWorkflows";

const ITEMS_PER_PAGE = 20;

type StatusFilter = "all" | "active" | "expired";

interface LockFilters {
  workflowStages?: string[];
  lockedByIds?: string[];
  status?: StatusFilter;
}

interface CaseLockRow {
  id: string;
  caseId: string;
  lockedAt?: string | Date | null;
  expiresAt?: string | Date | null;
  workflowStage?: string | null;
  lockReason?: string | null;
  case?: { id: string; caseNumber?: string } | null;
  lockedBy?: { id: string; fullName?: string } | null;
}

const formatStage = (stage: string) =>
  stage
    .replace(/-/g, " ")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export default function CaseLocksPage() {
  const { data: locks, isLoading, isError, refetch } = useAllLocks();
  const forceReleaseMutation = useForceReleaseLock();

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<LockFilters>({ status: "all" });
  const [pendingFilters, setPendingFilters] = useState<LockFilters>({
    status: "all",
  });
  const [unlockTarget, setUnlockTarget] = useState<CaseLockRow | null>(null);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);

  const rows: CaseLockRow[] = useMemo(
    () => (Array.isArray(locks) ? (locks as CaseLockRow[]) : []),
    [locks]
  );

  const stageOptions = useMemo(() => {
    const seen = new Set<string>();
    rows.forEach((lock) => {
      if (lock.workflowStage) seen.add(lock.workflowStage);
    });
    return Array.from(seen)
      .map((stage) => ({ value: stage, label: formatStage(stage) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const userOptions = useMemo(() => {
    const seen = new Map<string, string>();
    rows.forEach((lock) => {
      const user = lock.lockedBy;
      if (user?.id) seen.set(user.id, user.fullName || "Unknown");
    });
    return Array.from(seen.entries())
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const filtered = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    const now = new Date();
    return rows.filter((lock) => {
      if (term) {
        const caseNumber = (lock.case?.caseNumber || "").toLowerCase();
        const lockedByName = (lock.lockedBy?.fullName || "").toLowerCase();
        const reason = (lock.lockReason || "").toLowerCase();
        const stage = (lock.workflowStage || "").toLowerCase();
        const matchesTerm =
          caseNumber.includes(term) ||
          lockedByName.includes(term) ||
          reason.includes(term) ||
          stage.includes(term);
        if (!matchesTerm) return false;
      }
      if (filters.workflowStages?.length) {
        if (
          !lock.workflowStage ||
          !filters.workflowStages.includes(lock.workflowStage)
        ) {
          return false;
        }
      }
      if (filters.lockedByIds?.length) {
        if (
          !lock.lockedBy?.id ||
          !filters.lockedByIds.includes(lock.lockedBy.id)
        ) {
          return false;
        }
      }
      if (filters.status && filters.status !== "all") {
        const isExpired = lock.expiresAt
          ? new Date(lock.expiresAt) < now
          : false;
        if (filters.status === "active" && isExpired) return false;
        if (filters.status === "expired" && !isExpired) return false;
      }
      return true;
    });
  }, [rows, debouncedSearch, filters]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, page]);

  useEffect(() => {
    if (page > 1 && page > totalPages) {
      setPage(1);
    }
  }, [page, totalPages]);

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      filters.workflowStages?.length ||
        filters.lockedByIds?.length ||
        (filters.status && filters.status !== "all")
    );
  }, [filters]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
      setPage(1);
    },
    []
  );

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleOpenFilters = useCallback(() => {
    setPendingFilters(filters);
    openFilterModal();
  }, [filters, openFilterModal]);

  const handleApplyFilters = useCallback(() => {
    setFilters(pendingFilters);
    setPage(1);
    closeFilterModal();
  }, [pendingFilters, closeFilterModal]);

  const handleClearPending = useCallback(() => {
    setPendingFilters({ status: "all" });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters({ status: "all" });
    setPage(1);
  }, []);

  const handleRequestUnlock = useCallback(
    (lock: CaseLockRow) => {
      setUnlockTarget(lock);
      openConfirmModal();
    },
    [openConfirmModal]
  );

  const handleCancelUnlock = useCallback(() => {
    closeConfirmModal();
    setUnlockTarget(null);
  }, [closeConfirmModal]);

  const handleConfirmUnlock = useCallback(() => {
    if (!unlockTarget) return;
    forceReleaseMutation.mutate(unlockTarget.caseId, {
      onSuccess: () => {
        notifications.show({
          title: "Lock Released",
          message: "The case lock has been force-released.",
          color: "green",
        });
        closeConfirmModal();
        setUnlockTarget(null);
        refetch();
      },
      onError: (error: Error) => {
        notifications.show({
          title: "Error",
          message: error.message || "Failed to release lock.",
          color: "red",
        });
      },
    });
  }, [unlockTarget, forceReleaseMutation, refetch, closeConfirmModal]);

  const handlePendingStagesChange = useCallback((value: string[]) => {
    setPendingFilters((prev) => ({ ...prev, workflowStages: value }));
  }, []);

  const handlePendingUsersChange = useCallback((value: string[]) => {
    setPendingFilters((prev) => ({ ...prev, lockedByIds: value }));
  }, []);

  const handlePendingStatusChange = useCallback((value: string | null) => {
    setPendingFilters((prev) => ({
      ...prev,
      status: (value as StatusFilter) || "all",
    }));
  }, []);

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Case Locks</Title>
              <Text size="sm" c="dimmed" mt="xs">
                All currently active case locks. Locks auto-expire after 5
                minutes of inactivity.
              </Text>
            </div>
          </Group>

          <Group align="stretch" grow>
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by case #, user, reason, or stage..."
                  value={search}
                  onChange={handleSearchChange}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Tooltip label="Refresh">
                    <ActionIcon
                      variant="subtle"
                      onClick={handleRefresh}
                      loading={isLoading}
                    >
                      <IconRefresh size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Button variant="light" onClick={handleOpenFilters} fullWidth>
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.workflowStages &&
                      filters.workflowStages.length > 0 && (
                        <Badge variant="light" color="blue">
                          {filters.workflowStages.length} Stage
                          {filters.workflowStages.length > 1 ? "s" : ""}
                        </Badge>
                      )}
                    {filters.lockedByIds && filters.lockedByIds.length > 0 && (
                      <Badge variant="light" color="grape">
                        {filters.lockedByIds.length} User
                        {filters.lockedByIds.length > 1 ? "s" : ""}
                      </Badge>
                    )}
                    {filters.status && filters.status !== "all" && (
                      <Badge
                        variant="light"
                        color={filters.status === "active" ? "orange" : "gray"}
                      >
                        {filters.status === "active" ? "Active" : "Expired"}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filtered.length} {filtered.length === 1 ? "lock" : "locks"}
                </Text>
              </Stack>
            </Paper>
          </Group>

          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Active Locks ({filtered.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}–
                      {Math.min(page * ITEMS_PER_PAGE, filtered.length)} of{" "}
                      {filtered.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : isError ? (
                <Center p="xl">
                  <Stack align="center" gap="xs">
                    <IconAlertTriangle
                      size={32}
                      color="var(--mantine-color-red-6)"
                    />
                    <Text c="red" size="sm">
                      Could not fetch active case locks. Try refreshing.
                    </Text>
                  </Stack>
                </Center>
              ) : filtered.length === 0 ? (
                <Center p="xl">
                  <Stack align="center" gap="xs">
                    <IconLockOpen
                      size={32}
                      color="var(--mantine-color-gray-5)"
                    />
                    <Text c="dimmed" size="sm">
                      {debouncedSearch || hasActiveFilters
                        ? "No locks match the current filters."
                        : "No cases are currently locked."}
                    </Text>
                  </Stack>
                </Center>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Case #</Table.Th>
                      <Table.Th>Locked By</Table.Th>
                      <Table.Th>Reason</Table.Th>
                      <Table.Th>Stage</Table.Th>
                      <Table.Th>Locked At</Table.Th>
                      <Table.Th>Expires At</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 140 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginated.map((lock) => {
                      const isExpired = lock.expiresAt
                        ? new Date(lock.expiresAt) < new Date()
                        : false;
                      const caseNumber = lock.case?.caseNumber;
                      const isReleasing =
                        forceReleaseMutation.isPending &&
                        forceReleaseMutation.variables === lock.caseId;
                      return (
                        <Table.Tr key={lock.id}>
                          <Table.Td>
                            {caseNumber ? (
                              <Anchor
                                component={Link}
                                to={`/cases/${lock.caseId}`}
                                size="sm"
                                fw={500}
                              >
                                {caseNumber}
                              </Anchor>
                            ) : (
                              <Text size="sm" c="dimmed">
                                —
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {lock.lockedBy?.fullName || "Unknown"}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text
                              size="sm"
                              c={lock.lockReason ? undefined : "dimmed"}
                            >
                              {lock.lockReason || "—"}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            {lock.workflowStage ? (
                              <Badge
                                variant="light"
                                size="sm"
                                tt="capitalize"
                                color="blue"
                              >
                                {lock.workflowStage.replace(/-/g, " ")}
                              </Badge>
                            ) : (
                              <Text size="xs" c="dimmed">
                                —
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {lock.lockedAt
                                ? dayjs(lock.lockedAt).format("MMM D, h:mm A")
                                : "—"}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c={isExpired ? "red" : undefined}>
                              {lock.expiresAt
                                ? dayjs(lock.expiresAt).format("MMM D, h:mm A")
                                : "—"}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={isExpired ? "gray" : "orange"}
                              variant="filled"
                              size="sm"
                            >
                              {isExpired ? "Expired" : "Active"}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Tooltip label="Force unlock">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                loading={isReleasing}
                                onClick={() => handleRequestUnlock(lock)}
                              >
                                <IconLockOpen size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        <Modal.Root
          opened={filterModalOpened}
          onClose={closeFilterModal}
          size="md"
          centered
          closeOnClickOutside={false}
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>Filter Case Locks</Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <Stack gap="md">
                <MultiSelect
                  label="Workflow Stage"
                  placeholder={
                    stageOptions.length === 0
                      ? "No stages available"
                      : "Select stages"
                  }
                  data={stageOptions}
                  value={pendingFilters.workflowStages || []}
                  onChange={handlePendingStagesChange}
                  searchable
                  clearable
                  disabled={stageOptions.length === 0}
                />
                <MultiSelect
                  label="Locked By"
                  placeholder={
                    userOptions.length === 0
                      ? "No users available"
                      : "Select users"
                  }
                  data={userOptions}
                  value={pendingFilters.lockedByIds || []}
                  onChange={handlePendingUsersChange}
                  searchable
                  clearable
                  disabled={userOptions.length === 0}
                />
                <Select
                  label="Status"
                  data={[
                    { value: "all", label: "All" },
                    { value: "active", label: "Active" },
                    { value: "expired", label: "Expired" },
                  ]}
                  value={pendingFilters.status || "all"}
                  onChange={handlePendingStatusChange}
                  allowDeselect={false}
                />
              </Stack>
            </Modal.Body>
            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={closeFilterModal}>
                Cancel
              </Button>
              <Group gap="xs">
                <Button
                  variant="subtle"
                  color="red"
                  onClick={handleClearPending}
                >
                  Clear All
                </Button>
                <Button onClick={handleApplyFilters}>Apply Filters</Button>
              </Group>
            </Group>
          </Modal.Content>
        </Modal.Root>

        <Modal.Root
          opened={confirmModalOpened}
          onClose={handleCancelUnlock}
          size="sm"
          centered
          closeOnClickOutside={false}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>Force Unlock Case</Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body>
              <Stack gap="md">
                <Text size="sm">
                  Force-unlock case{" "}
                  <strong>
                    {unlockTarget?.case?.caseNumber || unlockTarget?.caseId}
                  </strong>
                  {unlockTarget?.lockedBy?.fullName && (
                    <>
                      {" "}
                      locked by{" "}
                      <strong>{unlockTarget.lockedBy.fullName}</strong>
                    </>
                  )}
                  ?
                </Text>
                <Text size="sm" c="dimmed">
                  They will lose their lock immediately and any unsaved work may
                  be lost.
                </Text>
              </Stack>
            </Modal.Body>
            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={handleCancelUnlock}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleConfirmUnlock}
                loading={forceReleaseMutation.isPending}
              >
                Force Unlock
              </Button>
            </Group>
          </Modal.Content>
        </Modal.Root>
      </Box>
    </Container>
  );
}
